import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Stethoscope } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { finishLogin } = useAuth();
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiErr(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name, password });
      finishLogin(data.token, data.user);
      toast.success("Practice activated! Welcome aboard.");
      navigate("/app/dashboard");
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background bg-radial-fade px-5">
      <Card className="w-full max-w-md rounded-2xl p-8 shadow-xl">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Stethoscope className="h-5 w-5" />
          </div>
          <div className="font-bold tracking-tight">Practice OS™</div>
        </div>
        {err ? (
          <p className="text-sm text-destructive" data-testid="invite-error">{err}</p>
        ) : !info ? (
          <p className="text-sm text-muted-foreground">Loading invite…</p>
        ) : (
          <>
            <h1 className="text-xl font-bold tracking-tight">Activate {info.practice_name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Set up your principal account for {info.email}.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Your name</Label>
                <Input id="name" data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw">Choose a password</Label>
                <Input id="pw" type="password" data-testid="invite-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
              </div>
              <Button type="submit" data-testid="invite-submit" className="w-full rounded-full" disabled={loading}>
                {loading ? "Activating…" : "Activate practice"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
